import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Overline, MaskedLines } from "./shared";
import { LightArt } from "./LightArt";

const PROJECTS = [
    { name: "Orbit", location: "Mumbai", type: "Kinetic Sculpture", scale: "4.2 m suspended", concept: "A slow constellation revolving above a hotel lobby.", variant: "rings", hue: "warm" },
    { name: "Flow", location: "Bengaluru", type: "Kinetic Light", scale: "18 m corridor", concept: "Light that travels like water along a workspace spine.", variant: "wave", hue: "cool" },
    { name: "Pulse", location: "Dubai", type: "Interactive Wall", scale: "9 m feature wall", concept: "A wall that answers footsteps with waves of light.", variant: "grid", hue: "mixed" },
    { name: "Veil", location: "New Delhi", type: "Motion Architecture", scale: "2-storey facade", concept: "A breathing skin of moving fins over glass.", variant: "rods", hue: "cool" },
    { name: "Infinity", location: "Goa", type: "Signature Installation", scale: "Resort atrium", concept: "An endless loop of mirrored motion and warm light.", variant: "halo", hue: "warm" },
    { name: "Bloom", location: "Hyderabad", type: "Kinetic Sculpture", scale: "2.8 m opening form", concept: "A mechanical flower that opens as visitors gather.", variant: "burst", hue: "warm" },
    { name: "Wave", location: "Singapore", type: "Kinetic Ceiling", scale: "320 elements", concept: "Hundreds of rods rolling like a slow tide overhead.", variant: "rods", hue: "mixed" },
    { name: "Convergence", location: "London", type: "Brand Installation", scale: "Flagship retail", concept: "Fragments of light assembling into a living mark.", variant: "burst", hue: "cool" },
    { name: "Motion Grid", location: "Pune", type: "Kinetic Wall", scale: "6 m matrix", concept: "A precise field of moving tiles in constant dialogue.", variant: "grid", hue: "warm" },
];

export const Portfolio = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
    const x = useTransform(scrollYProgress, [0, 1], ["2%", "-72%"]);

    return (
        <section id="work" ref={ref} data-testid="portfolio-section" className="relative border-t border-white/5" style={{ height: "380vh" }}>
            <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden">
                <div className="mx-auto w-full max-w-[1600px] px-6 md:px-12">
                    <Overline>Installation Stories</Overline>
                    <h2 className="mt-8 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
                        <MaskedLines lines={["Things we make move."]} />
                    </h2>
                </div>
                <motion.div style={{ x }} className="mt-14 flex w-max gap-6 pl-6 md:mt-20 md:pl-12">
                    {PROJECTS.map((p) => (
                        <article
                            key={p.name}
                            data-testid={`project-${p.name.toLowerCase().replace(/\s/g, "-")}`}
                            className="group relative h-[46vh] w-[78vw] shrink-0 overflow-hidden border border-white/10 sm:w-[52vw] md:h-[52vh] lg:w-[34vw]"
                        >
                            <LightArt variant={p.variant} hue={p.hue} className="h-full w-full opacity-80 transition-opacity duration-700 group-hover:opacity-100" />
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-6 md:p-8">
                                <div className="flex items-baseline justify-between gap-4">
                                    <h3 className="font-display text-3xl font-bold uppercase tracking-tight text-white md:text-4xl">{p.name}</h3>
                                </div>
                                <p className="mt-3 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#FFE8B6]/70">
                                    {p.type} · {p.scale}
                                </p>
                                <p className="mt-3 max-w-sm text-sm font-light leading-relaxed text-white/60">{p.concept}</p>
                            </div>
                        </article>
                    ))}
                    <div className="flex h-[46vh] w-[40vw] shrink-0 items-center md:h-[52vh]">
                        <p className="max-w-xs font-display text-2xl font-bold uppercase leading-tight tracking-tight text-white/40">
                            Every space gets its own idea.
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
